import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import logger from '../utils/logger';
import { Clock, User as UserIcon, Activity, ArrowRight } from 'lucide-react';

/**
 * Compact "Change History" panel — fetches /api/audit/logs/resource/{type}/{id}
 * Renders timeline of actor + description + before/after.
 *
 * Props:
 *   resourceType: e.g. 'lead', 'employee', 'payroll'
 *   resourceId:   the resource's ObjectId string
 *   limit:        default 25
 *   compact:      true → use small spacing for inline embeds
 */
const ChangeHistory = ({ resourceType, resourceId, limit = 25, compact = false }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!resourceType || !resourceId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/audit/logs/resource/${resourceType}/${resourceId}?limit=${limit}`,
                { withCredentials: true },
            );
            setRows(data.rows || []);
            setError(null);
        } catch (e) {
            logger.error('change-history fetch', e);
            setError(e.response?.data?.detail || 'Unable to load history');
        } finally {
            setLoading(false);
        }
    }, [resourceType, resourceId, limit]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const fmt = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return <div className={`text-xs text-slate-400 ${compact ? 'py-2' : 'py-4'}`} data-testid="change-history-loading">Loading history...</div>;
    }
    if (error) {
        return <div className="text-xs text-red-600 py-2" data-testid="change-history-error">{error}</div>;
    }
    if (!rows.length) {
        return <div className={`text-xs text-slate-400 ${compact ? 'py-2' : 'py-4'}`} data-testid="change-history-empty">No changes recorded yet.</div>;
    }

    return (
        <div className={`space-y-2 ${compact ? '' : 'mt-2'}`} data-testid={`change-history-${resourceType}-${resourceId}`}>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                <Activity className="h-3.5 w-3.5" /> Change History · {rows.length} event{rows.length !== 1 ? 's' : ''}
            </div>
            <ul className="space-y-2 border-l-2 border-slate-200 pl-3 max-h-72 overflow-y-auto pr-1">
                {rows.map((row) => {
                    const isBusiness = row.is_business_event;
                    const desc = row.description || `${row.method || ''} ${row.path || ''}`.trim();
                    return (
                        <li key={row._id} className="text-xs" data-testid={`history-row-${row._id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-800 leading-snug">{desc}</p>
                                    <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                                        <UserIcon className="h-3 w-3" /> {row.actor_name || 'Unknown'}
                                        {row.actor_role && <span className="text-slate-400">({row.actor_role})</span>}
                                        <Clock className="h-3 w-3 ml-1" /> {fmt(row.timestamp)}
                                    </p>
                                    {isBusiness && row.before && row.after && (
                                        <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 font-mono">
                                            {Object.entries(row.after).slice(0, 2).map(([k, v]) => {
                                                const oldV = row.before[k];
                                                if (oldV === v || v === undefined || v === null) return null;
                                                return (
                                                    <span key={k}>
                                                        {k}: <span className="text-red-500 line-through">{String(oldV ?? '∅')}</span>
                                                        <ArrowRight className="inline h-2.5 w-2.5 mx-1" />
                                                        <span className="text-emerald-600 font-medium">{String(v)}</span>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                {row.method && !isBusiness && (
                                    <span className={`text-[9px] font-bold tabular-nums px-1.5 py-0.5 rounded ${
                                        row.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                                        row.method === 'PUT' ? 'bg-amber-100 text-amber-700' :
                                        row.method === 'PATCH' ? 'bg-purple-100 text-purple-700' :
                                        row.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                                        'bg-slate-100 text-slate-600'
                                    }`}>
                                        {row.method}{row.status_code ? ` · ${row.status_code}` : ''}
                                    </span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default ChangeHistory;
